import {
  type ClientApplicationsQuery,
  useClientApplicationsQuery,
} from "@graphql/client-applications/ClientApplications.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import { DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS } from "../UI/ClientApplicationsTableColumns";
import {
  CLIENT_APPLICATION_TABLE_COLUMN_LABELS,
  ClientApplicationTableColumns,
} from "../UI/ClientApplicationsTableColumns";

type Props = {
  tableId: string;
  clientId: string;
  exportType?: "csv";
  // Filter parameters - same as the table component
  reviewState?: TenantedClientApplicationReviewStateChoices[];
  managedBy?: TenantedClientApplicationManagedByChoices[];
  supportedBy?: TenantedClientApplicationSupportedByChoices[];
  businessOwnerId?: string;
  productNameSearch?: string;
  parentCategoryId?: string;
  createdAt_Gte?: string;
  createdAt_Lte?: string;
};

export const useClientApplicationTableExport = ({
  tableId,
  clientId,
  exportType = "csv",
  reviewState,
  managedBy,
  supportedBy,
  businessOwnerId,
  productNameSearch,
  parentCategoryId,
  createdAt_Gte,
  createdAt_Lte,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const { getColumnSettings, getColumnOrder } = useTableColumnSettings({
    tableId,
  });

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const BATCH_SIZE = 100;
      const allApplications: NonNullable<
        ClientApplicationsQuery["clientApplications"]
      >["edges"] = [];
      let hasNextPage = true;
      let offset = 0;

      // Fetch data in batches of 100 records
      while (hasNextPage) {
        const data = await queryClient.fetchQuery({
          queryKey: useClientApplicationsQuery.getKey({
            clientId,
            first: BATCH_SIZE,
            offset: offset,
            reviewState_In:
              reviewState && reviewState.length > 0 ? reviewState : undefined,
            managedBy_In:
              managedBy && managedBy.length > 0 ? managedBy : undefined,
            supportedBy_In:
              supportedBy && supportedBy.length > 0 ? supportedBy : undefined,
            businessOwner_In: businessOwnerId ? [businessOwnerId] : undefined,
            resource_ProductName_Icontains: productNameSearch || undefined,
            parentCategoryId: parentCategoryId || undefined,
            createdAt_Gte: createdAt_Gte || undefined,
            createdAt_Lte: createdAt_Lte || undefined,
            resource_IsPrimaryResource: true,
          }),
          queryFn: useClientApplicationsQuery.fetcher({
            clientId,
            first: BATCH_SIZE,
            offset: offset,
            reviewState_In:
              reviewState && reviewState.length > 0 ? reviewState : undefined,
            managedBy_In:
              managedBy && managedBy.length > 0 ? managedBy : undefined,
            supportedBy_In:
              supportedBy && supportedBy.length > 0 ? supportedBy : undefined,
            businessOwner_In: businessOwnerId ? [businessOwnerId] : undefined,
            resource_ProductName_Icontains: productNameSearch || undefined,
            parentCategoryId: parentCategoryId || undefined,
            createdAt_Gte: createdAt_Gte || undefined,
            createdAt_Lte: createdAt_Lte || undefined,
            resource_IsPrimaryResource: true,
          }),
        });

        const edges = data.clientApplications?.edges || [];
        allApplications.push(...edges);

        // Check if we have more data to fetch
        hasNextPage = edges.length === BATCH_SIZE;
        offset += BATCH_SIZE;
      }

      const columnSettings =
        getColumnSettings() ?? DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS;
      const columnOrder = getColumnOrder() as
        | ClientApplicationTableColumns[]
        | undefined;

      // Get enabled columns
      const enabledColumns = Object.entries(columnSettings)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value)
        .map(([key]) => key as ClientApplicationTableColumns);

      // Sort columns according to user's preferred order if available
      let selectedColumns: ClientApplicationTableColumns[];
      if (columnOrder && columnOrder.length > 0) {
        // Use the user's column order, but only include enabled columns
        selectedColumns = columnOrder.filter((col) =>
          enabledColumns.includes(col),
        );

        // Add any enabled columns that aren't in the order (shouldn't happen, but safety check)
        const missingColumns = enabledColumns.filter(
          (col) => !selectedColumns.includes(col),
        );
        selectedColumns.push(...missingColumns);
      } else {
        // Fall back to default order
        selectedColumns = enabledColumns;
      }

      const clientApplications = allApplications
        .map((edge) => {
          if (!edge?.node) return null;

          const application: Record<string, unknown> = {};

          selectedColumns.forEach((column) => {
            const label = CLIENT_APPLICATION_TABLE_COLUMN_LABELS[column];

            switch (column) {
              case ClientApplicationTableColumns.productName:
                application[label] = edge.node?.resource?.productName || "";
                break;
              case ClientApplicationTableColumns.reviewState:
                application[label] = edge.node?.reviewState || "";
                break;
              case ClientApplicationTableColumns.managedBy:
                application[label] = edge.node?.managedBy || "";
                break;
              case ClientApplicationTableColumns.supportedBy:
                application[label] = edge.node?.supportedBy || "";
                break;
              case ClientApplicationTableColumns.usedBy:
                // TODO: Update to use resourceGroupApplications.stats.totalEmployees when available
                application[label] = 0;
                break;
              case ClientApplicationTableColumns.category: {
                const parentName =
                  edge.node?.resource?.publicResource?.category?.parent?.name;
                const categoryName =
                  edge.node?.resource?.publicResource?.category?.name;
                application[label] = parentName || categoryName || "";
                break;
              }
              case ClientApplicationTableColumns.vendorName:
                application[label] = edge.node?.resource?.vendorName || "";
                break;
              case ClientApplicationTableColumns.businessOwner:
                application[label] = edge.node?.businessOwner?.id || "-";
                break;
              case ClientApplicationTableColumns.createdAt:
                application[label] = edge.node?.createdAt
                  ? new Date(edge.node.createdAt).toLocaleDateString()
                  : "";
                break;
              case ClientApplicationTableColumns.updatedAt:
                application[label] = edge.node?.updatedAt
                  ? new Date(edge.node.updatedAt).toLocaleDateString()
                  : "";
                break;
              case ClientApplicationTableColumns.notes:
                application[label] = edge.node?.notes || "";
                break;
              case ClientApplicationTableColumns.notifications:
                // TODO: Update to use resourceGroupApplications.stats.totalNotifications when available
                application[label] = 0;
                break;
              case ClientApplicationTableColumns.productDescription:
                application[label] =
                  edge.node?.resource?.productDescription || "";
                break;
              case ClientApplicationTableColumns.vendorDescription:
                application[label] =
                  edge.node?.resource?.vendorDescription || "";
                break;
              default:
                application[label] = "";
            }
          });

          return application;
        })
        .filter(
          (application): application is Record<string, unknown> =>
            application !== null,
        );

      if (!clientApplications || clientApplications.length === 0) {
        throw new Error("No client applications found");
      }

      if (exportType === "csv") {
        const csv = json2csv(clientApplications);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `client-applications-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    getColumnSettings,
    getColumnOrder,
    clientId,
    reviewState,
    managedBy,
    supportedBy,
    businessOwnerId,
    productNameSearch,
    parentCategoryId,
    createdAt_Gte,
    createdAt_Lte,
    isExporting,
  ]);

  return useMemo(
    () => ({ exportData, isExporting }),
    [exportData, isExporting],
  );
};
